import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Inbox, RotateCw } from "lucide-react";

export function PageLoader() {
  const { t } = useTranslation();
  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="page-loader">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-hair border-t-cyan rounded-full animate-spin" />
        <span className="font-techmono text-xs uppercase tracking-widest text-mut">{t("common.loading")}</span>
      </div>
    </div>
  );
}

export function Skeleton({ className = "" }) {
  return <div className={`bg-white/5 animate-pulse ${className}`} />;
}

export function EmptyState({ message, icon: Icon = Inbox, action }) {
  return (
    <div className="py-16 text-center border border-dashed border-hair" data-testid="empty-state">
      <Icon className="w-8 h-8 text-mut mx-auto" strokeWidth={1.5} />
      <p className="mt-4 text-mut">{message}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ message }) {
  return (
    <div className="py-16 text-center border border-dashed border-danger/40" data-testid="error-state">
      <AlertTriangle className="w-8 h-8 text-danger mx-auto" strokeWidth={1.5} />
      <p className="mt-4 text-mut">{message}</p>
    </div>
  );
}

/**
 * Full-page error + retry for a failed data fetch (e.g. /pages/home).
 * Keeps the user from being stuck on an infinite spinner.
 */
export function PageError({ onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="page-error">
      <div className="text-center max-w-md mx-auto px-4">
        <AlertTriangle className="w-10 h-10 text-danger mx-auto" strokeWidth={1.5} />
        <h2 className="mt-5 font-display uppercase tracking-wide text-fg text-2xl">
          {t("common.errorTitle")}
        </h2>
        <p className="mt-3 text-mut">{t("common.errorText")}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            data-testid="page-error-retry"
            className="btn-primary mt-6 inline-flex items-center gap-2"
          >
            <RotateCw className="w-4 h-4" />
            {t("common.retry")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Inline, non-blocking error banner. Render INSIDE a page when an optional or
 * non-critical fetch fails — the rest of the page keeps working and the user
 * can retry just that section.
 */
export function ErrorBanner({ message, onRetry, testId = "error-banner" }) {
  const { t } = useTranslation();
  return (
    <div
      className="container-x my-6 border border-danger/40 bg-danger/5 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
      role="alert"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 text-fg text-sm">
        <AlertTriangle className="w-5 h-5 text-danger shrink-0" strokeWidth={1.5} />
        <span>{message || t("common.errorText")}</span>
      </div>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
          className="inline-flex items-center gap-1.5 text-accent font-techmono uppercase tracking-widest text-xs hover:opacity-80"
        >
          <RotateCw className="w-3.5 h-3.5" />
          {t("common.retry")}
        </button>
      ) : null}
    </div>
  );
}

/** Card-shaped skeleton placeholder for service grids etc. */
export function CardSkeleton({ className = "" }) {
  return (
    <div className={`glass-card overflow-hidden ${className}`} data-testid="card-skeleton">
      <div className="aspect-[4/3] bg-white/5 animate-pulse" />
      <div className="p-6 space-y-3">
        <div className="h-3 w-1/3 bg-white/5 animate-pulse" />
        <div className="h-5 w-3/4 bg-white/5 animate-pulse" />
        <div className="h-3 w-full bg-white/5 animate-pulse" />
        <div className="h-3 w-5/6 bg-white/5 animate-pulse" />
      </div>
    </div>
  );
}
